import { stat } from "fs";
import { compile, derivative, parse } from "mathjs";
import Stat from "models/Stat";

interface StatsCache {
    [key: number]: Array<number>;
}

interface StatFormulaDepsCache {
    [key: number]: Array<string>
}

interface ReverseStatFormulaDepsCache {
    [key: number]: Array<number>
}

export default class StatComputationService {
    stats: Array<Stat> = [];
    computedCache: StatsCache = {};

    /**
     * Represents a map of [stat.id] to a list of formulas of other stats it depends on.
     */
    formulaDepsCache: StatFormulaDepsCache = {};

    /**
     * Represents a map of {stat.id} to a list of stat ids that depend on it.
     */
    reverseFormulaDepsCache: ReverseStatFormulaDepsCache = {};


    /**
     * Initializes the current stat computation context with a list of relevant stats.
     * @param stats 
     */
    initContext(stats: Array<Stat> = []) {
        this.stats = stats.slice();
        this.computedCache = {};
        this.formulaDepsCache = {};
        this.reverseFormulaDepsCache = {};

        // Initialize the deps caches
        stats.forEach((stat) => this.updateFormulaDepsCache(stat));
    }

    updateFormulaDepsCache(stat: Stat) {
        if (stat === null) {
            return;
        }

        const variables = this.extractVariables(stat.formula.toLowerCase());
        const validStats = this.stats.filter(st => variables.has(st.name.toLowerCase()));
        this.formulaDepsCache[stat.id] = validStats.map(stat => stat.formula.toLowerCase());

        // Add this stat into the reverse deps cache for any new variables
        validStats.forEach(validStat => {
            if (!this.reverseFormulaDepsCache[validStat.id]) {
                this.reverseFormulaDepsCache[validStat.id] = [];
            }

            if (!this.reverseFormulaDepsCache[validStat.id].includes(stat.id)) {
                this.reverseFormulaDepsCache[validStat.id].push(stat.id);
            }
        });

        // Update formula deps for other formulas that depend on it
        if (this.reverseFormulaDepsCache[stat.id]) {
            this.reverseFormulaDepsCache[stat.id].forEach(statId => {
                this.updateFormulaDepsCache(this.stats.find(st => st.id == statId));
            });
        } else {
            this.reverseFormulaDepsCache[stat.id] = [];
        }
    }

    deleteStatFormulaDepsCacheId(id: number) {
        // Remove it from the reverse deps list of other variables
        for (const statId in this.reverseFormulaDepsCache) {
            const index = this.reverseFormulaDepsCache[statId].indexOf(id);
            if (index >= 0) {
                this.reverseFormulaDepsCache[statId].splice(index, 1);
            }
        }

        delete this.reverseFormulaDepsCache[id];
        delete this.formulaDepsCache[id];
    }

    getFormulaDeps(stat: Stat) {
        return this.formulaDepsCache[stat.id] ?? null;
    }

    /**
     * Computes all the values for the provided {@link Stat}.
     * @param stat 
     * @param rowIndex 
     * @param cache 
     * @param currentFormula 
     * @param currentCompiledFormula 
     */
    computeStat(stat: Stat, rowIndex: number, cache: Object = {}, currentFormula?: string, currentCompiledFormula?): number {
        if (rowIndex > stat.maxIndex || rowIndex < stat.minIndex) {
            return 0;
        }

        currentFormula ??= stat.formula;
        currentFormula = currentFormula.toLowerCase();
        currentCompiledFormula ??= compile(currentFormula);

        if (currentFormula.trim().length == 0) {
            return 0;
        }

        const variables = this.extractVariables(currentFormula);

        if (!cache.hasOwnProperty(rowIndex)) {
            const params = {
                row: rowIndex,
                next_row: rowIndex + 1,
                prev_row: rowIndex - 1
            };

            if ((new RegExp('.*next(?!_).*')).test(currentFormula)) {
                params['next'] = this.computeStat(stat, rowIndex + 1, cache, currentFormula, currentCompiledFormula);
            }

            if ((new RegExp('.*prev(?!_).*')).test(currentFormula)) {
                params['prev'] = this.computeStat(stat, rowIndex - 1, cache, currentFormula, currentCompiledFormula);
            }

            variables.forEach(variable => {
                if (variable.toLowerCase() === stat.name.toLowerCase()) {
                    return;
                }

                const validStat = this.stats.find(stat => stat.name.toLowerCase() === variable.toLowerCase());
                if (validStat) {
                    params[variable] = this.computeStat(validStat, rowIndex, {});
                }
            });

            cache[rowIndex] = stat.hasValue(rowIndex) ?
                stat.getValue(rowIndex) :
                currentCompiledFormula.evaluate(params);

            // Update the engine cache
            if (!this.computedCache[stat.id]) {
                this.computedCache[stat.id] = [];
            }
            this.computedCache[stat.id][rowIndex] = cache[rowIndex];
        }

        return cache[rowIndex];
    };

    /**
     * Extracts non-special variable names from a given formula string.
     * 
     * @param formula 
     */
    extractVariables(formula: string): Set<string> {
        const newVariables = new Set<string>();
        const ignoreNames = ['row', 'next', 'prev', 'next_row', 'prev_row'];
        parse(formula.toLowerCase()).traverse((node, _, parent) => {
            if (node.isSymbolNode
                && (parent?.name !== node.name)
                && !ignoreNames.includes(node.name)) {
                newVariables.add(node.name);
            }
        });
        return newVariables;
    }

}